// RH-PAIE-2C — Sélecteur de catégorie salariale recherchable, groupé par famille.
// Remplace le <select> à 20+ options plates utilisé jusqu'ici dans le dossier salarial,
// l'augmentation et la promotion. Catégories chargées depuis rh.categories_salariales
// (GET /api/paie/categories-salariales) — jamais codées en dur ici (dynamic-by-default).
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { humaniserFamilleProfessionnelle } from '../constants/libellesHumains';

const API = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
const VERT = '#1B5E20';
const GRIS_BORD = '#E0E0E0';

// Cache module-level : plusieurs instances du sélecteur sur un même écran (ex. modale
// promotion) ne déclenchent qu'un seul appel réseau.
let _cacheCategories = null;
let _cachePromise = null;
export async function chargerCategories() {
  if (_cacheCategories) return _cacheCategories;
  if (!_cachePromise) {
    _cachePromise = axios.get(`${API}/paie/categories-salariales`).then((r) => {
      _cacheCategories = r.data?.data || [];
      return _cacheCategories;
    });
  }
  return _cachePromise;
}

// RH-PAIE-2G — le cache module-level ci-dessus n'était jamais invalidé après une classification
// réussie : un sélecteur déjà monté dans la session continuait d'afficher la liste chargée avant
// la classification. À appeler après toute écriture sur rh.categories_salariales (classification,
// et plus généralement toute mutation future de ce référentiel) pour forcer un rechargement au
// prochain appel de chargerCategories().
export function invaliderCacheCategories() {
  _cacheCategories = null;
  _cachePromise = null;
}

function GroupeLabel({ texte }) {
  return (
    <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.04em', color: '#999', padding: '8px 12px 4px', fontWeight: 700 }}>
      {texte}
    </div>
  );
}

function Option({ cat, active, onClick }) {
  return (
    <div
      role="option"
      aria-selected={active}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      style={{
        padding: '8px 12px', cursor: 'pointer', fontSize: 13,
        background: active ? '#E8F5E9' : 'transparent',
        color: active ? VERT : '#333', fontWeight: active ? 700 : 400,
      }}
    >
      {cat.libelle} <span style={{ color: '#999', fontSize: 11.5 }}>({cat.code})</span>
    </div>
  );
}

// RH-PAIE-2E §4 — familleProfessionnelle : quand fourni, ne propose que les catégories de
// cette famille contrôlée (OUVRIERS/EMPLOYES/AGENTS_MAITRISE/CADRES) — jamais toute la liste
// mélangée. Sans ce filtre (comportement hérité), toutes les catégories restent proposées,
// groupées par famille professionnelle humanisée (y compris "À classer par la DRH").
export default function CategorieSelector({ value, onChange, label, allowEmpty = true, disabled = false, familleProfessionnelle }) {
  const [categories, setCategories] = useState([]);
  const [ouvert, setOuvert] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [surligne, setSurligne] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => { chargerCategories().then(setCategories); }, []);

  useEffect(() => {
    function fermerSiExterieur(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOuvert(false);
    }
    document.addEventListener('mousedown', fermerSiExterieur);
    return () => document.removeEventListener('mousedown', fermerSiExterieur);
  }, []);

  const categoriesDeLaFamille = familleProfessionnelle
    ? categories.filter((c) => c.famille_professionnelle === familleProfessionnelle)
    : categories;
  const categorieActuelle = categoriesDeLaFamille.find((c) => c.code === value);
  const filtrees = categoriesDeLaFamille.filter((c) =>
    !recherche.trim() || c.libelle.toLowerCase().includes(recherche.toLowerCase()) || c.code.toLowerCase().includes(recherche.toLowerCase())
  );
  const groupes = filtrees.reduce((acc, c) => {
    const cle = humaniserFamilleProfessionnelle(c.famille_professionnelle);
    (acc[cle] = acc[cle] || []).push(c);
    return acc;
  }, {});
  const listeAPlat = filtrees;

  const selectionner = useCallback((code) => {
    onChange(code);
    setOuvert(false);
    setRecherche('');
  }, [onChange]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOuvert(true); setSurligne((s) => Math.min(s + 1, listeAPlat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSurligne((s) => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (listeAPlat[surligne]) selectionner(listeAPlat[surligne].code); }
    else if (e.key === 'Escape') { setOuvert(false); }
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>{label}</label>}
      <input
        role="combobox"
        aria-expanded={ouvert}
        disabled={disabled}
        placeholder={allowEmpty ? 'Rechercher une catégorie…' : 'Rechercher une catégorie… *'}
        value={ouvert ? recherche : (categorieActuelle ? `${categorieActuelle.libelle} (${categorieActuelle.code})` : (value || ''))}
        onFocus={() => { setOuvert(true); setRecherche(''); }}
        onChange={(e) => { setRecherche(e.target.value); setSurligne(0); }}
        onKeyDown={onKeyDown}
        style={{ width: '100%', padding: '8px 12px', border: `1px solid ${GRIS_BORD}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: disabled ? '#f5f5f5' : '#fff' }}
      />
      {ouvert && (
        <div style={{ position: 'absolute', zIndex: 50, top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: `1px solid ${GRIS_BORD}`, borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.12)', maxHeight: 260, overflowY: 'auto' }}>
          {allowEmpty && (
            <Option cat={{ code: '', libelle: '— Aucune —' }} active={surligne === -1} onClick={() => selectionner('')} />
          )}
          {Object.keys(groupes).length === 0 && <div style={{ padding: 12, fontSize: 12.5, color: '#999' }}>Aucune catégorie ne correspond.</div>}
          {Object.entries(groupes).map(([famille, cats]) => (
            <div key={famille}>
              <GroupeLabel texte={famille} />
              {cats.map((c) => (
                <Option key={c.code} cat={c} active={listeAPlat[surligne]?.code === c.code} onClick={() => selectionner(c.code)} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
